import { Box, Popover, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import EnergyFlowIndicator from './EnergyFlowIndicator'
import { Variant } from '@mui/material/styles/createTypography'
import { IndicatorColor, LiveItemType } from './types'
import LiveViewBadge from '../../Libs/live-view/components/LiveViewBadge'
import { LiveViewItemSide } from 'src/Libs/live-view'
import { DASHBOARD_LIVE_VIEW_ITEM_PREFIX, DASHBOARD_LIVE_VIEW_VALUE_PREFIX } from 'src/Libs/qa/ids'
interface Props {
  icon: React.ReactNode
  rotateIcon?: number
  label: string
  translationY: number
  value: string
  directionIsReversed?: boolean
  doNotFlow?: boolean
  energyFlowIndicatorsCount?: number
  labelVariant: Variant
  valueVariant: Variant
  inColor: IndicatorColor
  outColor: IndicatorColor
  badgeAmount?: number
  popoverContent?: React.ReactNode
  children?: React.ReactNode
  overlayIcon?: React.ReactNode
  side: LiveViewItemSide
  type: LiveItemType
}

interface StyleProps {
  rotateIcon?: number
  translationY: number
  active?: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => {
  const height = props.translationY
  const iconRotation = props.rotateIcon ?? 0
  const itemRotation = iconRotation * -1

  return {
    icon: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      position: 'absolute',
      bottom: 0,
      // 30px is half of the width of the item
      left: 'calc(50% - 30px)',
      transformOrigin: 'bottom center',
      maxWidth: 60,
      width: 60,
    },
    rotateFirstIcon: {
      transform: `translateY(${height}px) rotate(${iconRotation}deg) translateY(${-height - 30}px)`,
      [theme.breakpoints.down('sm')]: {
        transform: `translateY(${height}px) rotate(${iconRotation}deg) translateY(${-height - 20}px)`,
      },
    },
    rotateFirstItem: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      transform: `rotate(${itemRotation}deg)`,
      transformOrigin: '50% calc(100% - 42px)',
      maxWidth: 60,
    },
    item: {
      width: 60,
      maxWidth: 60,
      height: 60,
      borderRadius: '50%',
      background: theme.palette.grey.grey06,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
      color: props.active ? theme.palette.liveView.liveViewItem : undefined,
    },
    iconHover: {
      transition: theme.transitions.create('all'),

      '&:hover': {
        border: `1px solid ${theme.palette.liveView.liveViewItem}`,
        transition: theme.transitions.create('all'),
        cursor: 'pointer',
      },
    },
    iconPopoverOpened: {
      border: `1px solid ${theme.palette.liveView.liveViewItem}`,
    },
  }
})

const LiveViewItem: React.FC<Props> = ({
  icon,
  rotateIcon,
  translationY,
  label,
  value,
  doNotFlow,
  directionIsReversed,
  energyFlowIndicatorsCount,
  children,
  labelVariant,
  valueVariant,
  inColor,
  outColor,
  badgeAmount,
  popoverContent,
  overlayIcon,
  side,
  type,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { classes, cx } = useStyles({ rotateIcon, translationY, active: !!value })
  const popoverIsOpen = Boolean(anchorEl)

  const theme = useTheme()
  const upSm = useMediaQuery(theme.breakpoints.up('sm'))
  const requiresMaxWidth = !upSm && side !== 'center'
  const maxWidth = requiresMaxWidth ? 60 : undefined

  const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => (popoverContent ? setAnchorEl(event.currentTarget) : undefined)
  const handleClose = (): void => setAnchorEl(null)

  const iconComponent = (
    <Box
      className={cx(classes.item, { [classes.iconHover]: Boolean(popoverContent), [classes.iconPopoverOpened]: popoverIsOpen })}
      onClick={handleClick}
    >
      {icon}
    </Box>
  )

  const item = (
    <Box className={cx(classes.rotateFirstIcon, classes.icon)} data-qa={`${DASHBOARD_LIVE_VIEW_ITEM_PREFIX}${type}`}>
      <Box className={classes.rotateFirstItem}>
        <Typography variant={labelVariant} align="center" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: maxWidth }}>
          {label}
        </Typography>
        <Typography variant={valueVariant} align="center" data-qa={`${DASHBOARD_LIVE_VIEW_VALUE_PREFIX}${type}`}>
          {value}
        </Typography>
        <LiveViewBadge iconComponent={iconComponent} overlayIcon={overlayIcon} badgeAmount={badgeAmount ?? 0} />
      </Box>
      <EnergyFlowIndicator
        animate={!doNotFlow}
        reverse={directionIsReversed}
        energyFlowIndicatorsCount={energyFlowIndicatorsCount}
        inColor={inColor}
        outColor={outColor}
      />
      {popoverContent && (
        <Popover
          anchorEl={anchorEl}
          open={popoverIsOpen}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        >
          {popoverContent}
        </Popover>
      )}
      {children}
    </Box>
  )
  return item
}

export default LiveViewItem
